import assert from 'assert';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HashRouter, Navigate, Route, Routes } from 'react-router-dom';
import { ButtonType } from './components/Button/Button.props';
import OverlayButton from './components/OverlayButton/OverlayButton';
import ProductPropertiesOverlay from './components/ProductPropertiesOverlay/ProductPropertiesOverlay';
import { GlobalContext } from './interfaces/GlobalContext/GlobalContext';
import { UrlPaths } from './interfaces/UrlPaths/UrlPaths';
import CartPage from './pages/CartPage/CartPage';
import CheckDataPage from './pages/CheckDataPage/CheckDataPage';
import CustomerDataPage from './pages/CustomerDataPage/CustomerDataPage';
import EndPage from './pages/EndPage/EndPage';
import CampaignOrderButton from './components/CampaignOrderButton/CampaignOrderButton';
import MultiTestFormPage from './pages/MultiTestFormPage/MultiTestFormPage';
import MultiTestEndPagePage from './pages/MultiTestEndPage/MultiTestEndPagePage';

const App = (props: JSX.IntrinsicAttributes & {
    type: string,
    magicNumber?: string,
    buttonTitle?: string,
    buttonClass?: string,
    fsResources?: string
}) => {
    const { t, i18n } = useTranslation('productPropertiesOverlay');
    const { i18nProvider, loginProvider } = useContext(GlobalContext);
    i18nProvider.setProps({ t: t, i18n: i18n });
    loginProvider.init();

    let closeButtonRef;

    // for multiTestFormPage and its children
    let campaignOrderButton;
    let pageDetails = {};

    // State to store the fetched JSON data
    const [fsRessources, setFsRessources] = useState(null);

    // Fetch JSON data on component mount
    useEffect(() => {
        fetch('/jportal/nav/juris-shop-pwa/fs-ressourcen.json')
            .then(response => response.json())
            .then(data => setFsRessources(data))
            .catch(error => console.error('Error fetching JSON:', error));
    }, []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(useContext(GlobalContext).utilityProvider.dispatchEventReactAppInitialized, []);

    // "browser-back-button"-fix: by reloading the page right after the navigation-event
    // to cleanup mismatching states, as if the user would've used the "intended" back-button.
    useEffect(() => {
        const handlePopState = () => window.location.reload();
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    });

    switch (props.type) {
        case "productCartPage":
            return (
                <HashRouter>
                    <Routes>
                        <Route path="/" element={
                            <Navigate to={UrlPaths.CARTPAGE} replace={true} />
                        } />
                        <Route path={UrlPaths.CARTPAGE} element={
                            <CartPage />
                        } />
                        <Route path={UrlPaths.CUSTOMERDATAPAGE} element={
                            <CustomerDataPage />
                        } />
                        <Route path={UrlPaths.CHECKDATAPAGE} element={
                            <CheckDataPage />
                        } />
                        <Route path={UrlPaths.ENDPAGE} element={
                            <EndPage />
                        } />
                    </Routes>
                </HashRouter>
            );
        case "productOverlayButton":
            assert(typeof props.magicNumber === 'string');
            closeButtonRef = React.createRef<HTMLButtonElement>();
            return (
                <OverlayButton label={props.buttonTitle}
                    closeButtonRef={closeButtonRef}
                    buttonClass={props.buttonClass}
                    type={props.buttonClass && props.buttonClass?.indexOf('text-icon-link') >= 0 ? ButtonType.TEXTICONLINK : ButtonType.CTABUTTON}>
                    <ProductPropertiesOverlay magicNumber={props.magicNumber} closeButtonRef={closeButtonRef} />
                </OverlayButton>
            );
        case "multiTestFormPage":
            campaignOrderButton = document.querySelector('.step2button');
            pageDetails = {
                campaignId: campaignOrderButton?.getAttribute('data-campaign-id'),
                pageType: campaignOrderButton?.getAttribute('data-page-type'),
                frameworkContract: campaignOrderButton?.getAttribute('data-framework-contract'),
                preselectMembership: campaignOrderButton?.getAttribute('data-preselect-membership'),
                preselectFirstCard: campaignOrderButton?.getAttribute('data-preselect-firstcard'),
                singleProduct: campaignOrderButton?.getAttribute('data-single-product'),
                fsId: campaignOrderButton?.getAttribute('data-fs-id'),
                headerImg: campaignOrderButton?.getAttribute('data-header-image'),
                headerImgSmall: campaignOrderButton?.getAttribute('data-header-image-small') ,
                fsRessources: fsRessources                
            }

            return (
                <HashRouter>
                    <Routes>
                        <Route path="/" element={
                            <Navigate to={UrlPaths.SELECTIONPAGE} replace={true} />
                        } />
                        <Route path={UrlPaths.SELECTIONPAGE} element={
                            <CampaignOrderButton pageDetails={pageDetails}/>
                        } />
                        <Route path={UrlPaths.CUSTOMERDATAPAGE} element={
                            <MultiTestFormPage pageDetails={pageDetails}/>                            
                        } />
                        <Route path={UrlPaths.ENDPAGE} element={
                            <MultiTestEndPagePage pageDetails={pageDetails} />                           
                        } />
                    </Routes>
                </HashRouter>
            )
        default:
            return (<></>);
    }
}

export default App;
